import { useEffect, useState } from "react";

// Canadian postal code: A1A 1A1 (space optional, case-insensitive).
const CA_POSTAL_RE = /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z][ -]?\d[ABCEGHJ-NPRSTV-Z]\d$/i;

// Split a stored one-line address back into its parts, best-effort. Used when
// editing an existing profile. Falls back to putting everything in line1.
function parseAddress(str) {
  const out = { line1: "", line2: "", city: "", postal_code: "" };
  if (!str) return out;
  const parts = str.split(",").map((p) => p.trim()).filter(Boolean);
  // Last part might be the postal code.
  if (parts.length && CA_POSTAL_RE.test(parts[parts.length - 1])) {
    out.postal_code = parts.pop().toUpperCase();
  }
  if (parts.length >= 3) {
    out.line1 = parts.shift();
    out.line2 = parts.shift();
    out.city = parts.join(", ");
  } else if (parts.length === 2) {
    out.line1 = parts[0];
    out.city = parts[1];
  } else if (parts.length === 1) {
    out.line1 = parts[0];
  }
  return out;
}

// Recompose the structured fields back into a single stored string, matching
// the shape the backend already expects.
export function composeAddress({ line1, line2, city, postal_code }) {
  const chunks = [];
  if (line1) chunks.push(line1.trim());
  if (line2) chunks.push(line2.trim());
  if (city) chunks.push(city.trim());
  if (postal_code) chunks.push(postal_code.trim().toUpperCase());
  return chunks.join(", ");
}

// Client-side validity check — mirrors what the backend expects.
export function isAddressComplete({ line1, city, postal_code }) {
  if (!line1?.trim() || !city?.trim() || !postal_code?.trim()) return false;
  if (!CA_POSTAL_RE.test(postal_code.trim())) return false;
  return true;
}

/**
 * Structured address input. Presents 4 fields (line1, line2, city, postal code)
 * and reports the composed one-line string back via onChange. Also formats the
 * postal code as "A1A 1A1" on blur.
 */
export default function AddressFields({
  value = "",
  onChange,
  disabled,
  testIdPrefix = "address",
  showLabels = true,
}) {
  const [parts, setParts] = useState(() => parseAddress(value));

  useEffect(() => {
    // If the parent hands us a completely different value (rare), resync.
    if (composeAddress(parts) !== (value || "")) {
      setParts(parseAddress(value));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const patch = (partial) => {
    const next = { ...parts, ...partial };
    setParts(next);
    onChange?.(composeAddress(next));
  };

  const formatPostal = (raw) => {
    const trimmed = (raw || "").replace(/\s+/g, "").toUpperCase();
    if (trimmed.length === 6) return `${trimmed.slice(0, 3)} ${trimmed.slice(3)}`;
    return trimmed;
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-6 gap-3">
      <div className="sm:col-span-4">
        {showLabels && <FieldLabel>Street address</FieldLabel>}
        <input
          data-testid={`${testIdPrefix}-line1`}
          value={parts.line1}
          onChange={(e) => patch({ line1: e.target.value })}
          placeholder="123 Main Street"
          disabled={disabled}
          required
          className={inputCls}
        />
      </div>
      <div className="sm:col-span-2">
        {showLabels && <FieldLabel optional>Apt / Suite</FieldLabel>}
        <input
          data-testid={`${testIdPrefix}-line2`}
          value={parts.line2}
          onChange={(e) => patch({ line2: e.target.value })}
          placeholder="Unit 5"
          disabled={disabled}
          className={inputCls}
        />
      </div>
      <div className="sm:col-span-4">
        {showLabels && <FieldLabel>City</FieldLabel>}
        <input
          data-testid={`${testIdPrefix}-city`}
          value={parts.city}
          onChange={(e) => patch({ city: e.target.value })}
          placeholder="Vancouver"
          disabled={disabled}
          required
          className={inputCls}
        />
      </div>
      <div className="sm:col-span-2">
        {showLabels && <FieldLabel>Postal code</FieldLabel>}
        <input
          data-testid={`${testIdPrefix}-postal`}
          value={parts.postal_code}
          onChange={(e) => patch({ postal_code: e.target.value.toUpperCase() })}
          onBlur={(e) => patch({ postal_code: formatPostal(e.target.value) })}
          placeholder="V6B 1A1"
          disabled={disabled}
          required
          maxLength={7}
          className={`${inputCls} tracking-widest`}
          autoComplete="postal-code"
        />
      </div>
    </div>
  );
}

const inputCls = "w-full px-3 py-2.5 text-sm border border-border rounded focus:border-primary bg-background";

function FieldLabel({ children, optional }) {
  return (
    <div className="flex items-baseline justify-between mb-1.5">
      <label className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">{children}</label>
      {optional && <span className="text-[11px] text-muted-foreground">Optional</span>}
    </div>
  );
}
